<template>
    <div
        class="relative flex h-10 w-10 items-center justify-center overflow-hidden"
    >
        <a
            class="relative -ml-1 cursor-pointer hover:cursor-pointer"
            :title="t('layout.cart')"
            rel="noopener"
            :href="link"
            :target="target"
            @click="handleClick"
        >
            <shopping-cart-icon class="h-6 w-6" />
            <span class="sr-only">
                {{
                    t('pages.cartLabel', { count: mostRecentCart?.count ?? 0 })
                }}
            </span>
            <span
                v-if="(mostRecentCart?.count ?? 0) > 0"
                class="border-transparent absolute -end-2 -top-1.5 inline-flex items-center justify-center border-2 bg-primary-500 px-1 text-xs font-bold text-white"
            >
                {{ mostRecentCart.count }}
            </span>
        </a>
    </div>
</template>

<script setup lang="ts">
import { ShoppingCartIcon } from '@heroicons/vue/24/outline'
import { useRpQuery } from '~/composables/graphql'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { graphql } from '~/resources/graphql'
import { computed, onMounted, onServerPrefetch, onUnmounted } from 'vue'
import { definePageMeta } from '#imports'
import { useNuxtApp, useRequestURL, useRoute } from '#app'
import { getRequestHost } from 'h3'
import { useI18n } from 'vue-i18n'
import {
    unsafe_facilityCartLink,
    unsafe_emptyLink,
    layoutModeLightboxLink
} from '~/composables/routing'
import { useOrganizationContext } from '~/composables/use-organization-context'

definePageMeta({
    layout: false
})

const { t } = useI18n({ useScope: 'global' })
const organizationContext = useOrganizationContext()

const url = useRequestURL()
const CartCountQueryKey = 'CartIconQuery'
const query = useRpQuery({ orgLevel: true })
const { data, suspense } = useQuery({
    queryKey: [CartCountQueryKey, url.hostname],
    queryFn: () =>
        query(
            graphql(`
                query CartIconQuery {
                    viewer {
                        mostRecentCart {
                            facility {
                                slug
                            }
                            count
                        }
                    }
                }
            `)
        )
})

const mostRecentCart = computed(() => {
    return data.value?.viewer?.mostRecentCart ?? null
})

const nuxt = useNuxtApp()
const locationOrigin = computed(() => {
    if (process.server && nuxt.ssrContext) {
        return getRequestHost(nuxt.ssrContext.event)
    } else if (location !== undefined) {
        return location.origin
    } else {
        return ''
    }
})

const link = computed(() => {
    if (mostRecentCart.value === null || mostRecentCart.value.count === 0) {
        return unsafe_emptyLink()
    } else {
        return unsafe_facilityCartLink(mostRecentCart.value.facility.slug)
    }
})

const target = computed(() => {
    if (route.query.embedMode === 'new-tab') {
        return '_blank'
    } else {
        return undefined
    }
})

const route = useRoute()
function handleClick(event: PointerEvent) {
    if (route.query.embedMode === 'lightbox') {
        event.preventDefault()
        parent.postMessage(
            'RPHQ::1::' +
                JSON.stringify({
                    key: 'lightbox',
                    backgroundColor: organizationContext.value.topBarColor,
                    url:
                        locationOrigin.value +
                        layoutModeLightboxLink(link.value)
                }),
            '*'
        )
    } else if (route.query.embedMode === 'same-tab') {
        event.preventDefault()
        parent.postMessage(
            'RPHQ::1::' +
                JSON.stringify({
                    key: 'parent-redirect',
                    url: locationOrigin.value + link.value
                }),
            '*'
        )
    }
}

const queryClient = useQueryClient()
function parentListener(event: MessageEvent) {
    try {
        if (
            typeof event.data === 'string' &&
            event.data.length > 9 &&
            event.data.substring(0, 9) === 'RPHQ::1::'
        ) {
            const message = JSON.parse(event.data.substring(9))
            if (message.key === 'refresh-cart-count') {
                queryClient.invalidateQueries({ queryKey: [CartCountQueryKey] })
            }
        }
    } catch (e) {}
}
onMounted(() => {
    if (parent) {
        parent.addEventListener('message', parentListener)
    }
})

onUnmounted(() => {
    if (parent) {
        parent.removeEventListener('message', parentListener)
    }
})

onServerPrefetch(async () => {
    await suspense()
})
</script>

<style>
body {
    background-color: transparent;
    font-size: 13px;
}
</style>
